import type { LegacyIngressDefinition } from './types'

/**
 * Legacy v3 surfaces that v4 does not serve yet, each with a truthful outcome instead of a silent
 * catch-all redirect. Split out of `registry.ts` purely for the 400-line ceiling — `registry.ts`
 * stays the single composition point and the only module anything else imports.
 *
 * `activation: 'permanent'` means a product decision keeps the surface in v3 for good; it carries
 * no `cutoverIssue` and must never be counted as pending work.
 */
export const PENDING_CUTOVER: readonly LegacyIngressDefinition[] = [
  {
    // `:workflowId?` — v3 declares `/workflow/:id?`, so the bare `/workflow` is a real, reachable
    // URL. With a required param it fell through to the catch-all (caught by v3RouteCoverage).
    // PRODUCT DECISION (ORP-20033): Workflow's list and detail screens are being switched off —
    // they are not coming to v4 and will not survive v3's shutdown. `retired`, not
    // `legacyHardNavigation`: telling a user "this lives in the classic app" would be a lie the
    // moment v3 goes away. The menu entry `/ai-workflows` is a separate retained surface (A).
    //
    // The replacement remains Summary because the retained `/ai-workflows` hub is a different
    // product entry, not a detail/list successor. This is NOT the silent Summary redirect PR #468
    // removed: the user gets a page that says the feature is retired and chooses to move on.
    id: 'workflow-detail',
    legacyName: 'WorkflowDetail',
    path: '/workflow/:workflowId?',
    outcome: {
      kind: 'retired',
      replacement: { name: 'Summary' },
      messageKey: 'shell.ingress.retired.description',
    },
  },
  {
    id: 'workflow-list',
    legacyName: 'WorkflowList',
    path: '/workflow-list/:legacyPath(.*)*',
    outcome: {
      kind: 'retired',
      replacement: { name: 'Summary' },
      messageKey: 'shell.ingress.retired.description',
    },
  },
  {
    // PRODUCT DECISION (2026-07-31): the admin panel's 46 legacy routes stay in v3 for good —
    // module 19 is not being built in v4. `permanent`, not `pending`: this wildcard must never
    // show up in the pending-cutover queue, and ORP-18797 is no longer its cutover issue.
    id: 'admin',
    legacyName: 'Admin',
    path: '/admin/:legacyPath(.*)*',
    outcome: {
      kind: 'legacyHardNavigation',
      activation: 'permanent',
    },
    preserveQuery: ['page', 'search', 'status'],
    preserveHash: true,
  },
  {
    // PRODUCT DECISION (ORP-20039): this short historical alias is not the canonical
    // account/workspace Creative Analysis landing entry. Preserve its existing explicit legacy
    // outcome; mapping `:analysisId` to a v4 detail page is ORP-18906's cutover scope.
    //
    // The decision above is ORP-20039's and stays credited here, but the OWNER field must name
    // whoever still has work: ORP-20039 delivered the canonical landing ingress and explicitly
    // punted this alias, so once it closes it would leave this row unowned. ORP-18906 — which
    // already owns `/creative-ai/:analysisId/:tab?` and `/compositions` below — is that owner.
    id: 'creative-analysis-ingress',
    legacyName: 'CreativeAnalysis',
    path: '/creative-analysis/:analysisId?',
    outcome: {
      kind: 'legacyHardNavigation',
      activation: 'pending',
      cutoverIssue: 'ORP-18906',
    },
    preserveQuery: ['tab'],
  },
  // ── Second audit tranche (ORP-20024, 2026-07-31) ──────────────────────────────────────────
  // Reproduced against production: https://stagingapp.orphex.co/get-started silently resolved to
  // /summary, because an unregistered legacy path falls through to `/:pathMatch(.*)*`. Paths below
  // are READ FROM v3's LIVE ROUTER (`$router.getRoutes()` on app.orphex.co), not transcribed —
  // three of them are nested where a reading of the source would have put them somewhere else.
  {
    id: 'budget-performance',
    legacyName: 'BudgetPerformance',
    path: '/budget-performance',
    outcome: {
      kind: 'legacyHardNavigation',
      activation: 'pending',
      cutoverIssue: 'ORP-18958',
    },
  },
  {
    id: 'compositions',
    legacyName: 'Compositions',
    path: '/compositions/:id?',
    outcome: {
      kind: 'legacyHardNavigation',
      activation: 'pending',
      cutoverIssue: 'ORP-18906',
    },
  },
]
